import { Box, Typography } from '@mui/material';
import plantumlEncoder from 'plantuml-encoder';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
	IDiagram,
	IKeyRolesFormData,
	IParsedTableFormData,
	ITableFormValue,
} from 'store/ticket-service/types';

import { orderDiagrams } from 'utils/orderDiagrams';
import { calculateTextDiff, formatTextForDisplay } from 'utils/textDiff';

import { NamespacesList, UseCaseTableFields } from 'core/enums/common';

import { regularTextColor, titleTextColor } from 'assets/styles/_palette';

import { DiagramImage } from 'components/TicketSteps/UsecasesStep/DiagramImage/DiagramImage';

export const UseCasesChangesDisplay = ({
	oldValue,
	newValue,
}: {
	oldValue: Record<string, any>;
	newValue: Record<string, any>;
}) => {
	const { t: changesHistoryTranslate } = useTranslation(NamespacesList['changesVersioning']);
	const { t: useCasesTranslate } = useTranslation(NamespacesList['useCasesStep']);
	const { t: generalStepTranslate } = useTranslation(NamespacesList['generalStep']);

	const getUpdatedValueStyles = ({
		oldValue,
		newValue,
		isDeleted,
		showStyles,
	}: {
		oldValue: string;
		newValue: string;
		isDeleted: boolean;
		showStyles: boolean;
	}) => {
		if (showStyles) {
			if (isDeleted) {
				return { backgroundColor: '#FB0B244C' };
			}

			if (oldValue?.trim() !== newValue?.trim()) {
				return { backgroundColor: '#01C8604C' };
			}
		}

		return {};
	};

	const renderTextWithDiff = (
		oldText: string,
		newText: string,
		displayText: string,
		isDeleted: boolean,
		isNewBlock: boolean
	) => {
		// Si supprimé, afficher avec la couleur rouge dans les deux blocs
		if (isDeleted) {
			return <span style={{ backgroundColor: '#FB0B244C' }}>{displayText}</span>;
		}

		// Si les textes sont identiques, pas besoin de calculer le diff
		if (!oldText || !newText || oldText.trim() === newText.trim()) {
			return displayText;
		}

		// Calculer le diff pour les deux blocs (ancien et nouveau) pour surligner les suppressions
		const diffParts = calculateTextDiff(oldText || '', newText || '');
		return formatTextForDisplay(diffParts, isNewBlock, isDeleted);
	};

	const getDiagramCodeFromLink = (link: string) => {
		if (link) return plantumlEncoder.decode(link.split('/').pop() || '');
		return null;
	};

	const isDiagramUpdated = ({
		newDiagram,
		oldDiagram,
		showStyles,
	}: {
		newDiagram: IDiagram;
		oldDiagram: IDiagram;
		showStyles: boolean;
	}) => {
		if (showStyles) {
			return (
				getDiagramCodeFromLink(newDiagram.pictureLink) !==
				getDiagramCodeFromLink(oldDiagram.pictureLink)
			);
		}

		return false;
	};

	const getUseCaseTableBlock = ({
		oldTable,
		newTable,
		showStyles,
	}: {
		oldTable: IParsedTableFormData;
		newTable: IParsedTableFormData;
		showStyles: boolean;
	}) => {
		const usedTable = showStyles ? newTable : oldTable;

		return (
			<Box
				className="rtl-able"
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: '16px',
					margin: '12px 0 24px',
				}}
			>
				{(Object.entries(usedTable) as Array<[UseCaseTableFields, Array<any>]>).map(
					([category, data], index) => (
						<Box
							className="rtl-able"
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: '8px',
							}}
							key={index}
						>
							<Box>
								<Typography
									sx={{
										fontSize: '1rem',
										fontWeight: 700,
										paddingBottom: '8px',
										color: titleTextColor,
									}}
								>{`${useCasesTranslate(`descriptionTable.${category}`)}:`}</Typography>
								{category === UseCaseTableFields['Key Roles']
									? data.map((keyRolesItem: IKeyRolesFormData, categoryItemIndex: number) =>
											Object.entries(keyRolesItem).map(
												([keyRolesCategory, keyRolesData], keyRolesDataIndex) => (
													<Box key={`${categoryItemIndex} ${keyRolesDataIndex}`}>
														<Typography
															sx={{
																fontSize: '1rem',
																fontWeight: 700,
																paddingBottom: '8px',
																color: titleTextColor,
															}}
														>{`${useCasesTranslate(
															`descriptionTable.${keyRolesCategory}`
														)}:`}</Typography>
														<Typography
															sx={{
																fontSize: '0.95rem',
																color: regularTextColor,
															}}
															key={keyRolesData}
														>
															{renderTextWithDiff(
																oldTable[category][categoryItemIndex]?.[
																	keyRolesCategory === 'Role' ? 'Role' : 'Responsibilities'
																]?.value || '',
																newTable[category][categoryItemIndex]?.[
																	keyRolesCategory === 'Role' ? 'Role' : 'Responsibilities'
																]?.value || '',
																keyRolesData.value || '',
																usedTable[category][categoryItemIndex]?.[
																	keyRolesCategory === 'Role' ? 'Role' : 'Responsibilities'
																]?.isDeleted || false,
																showStyles
															)}
														</Typography>
													</Box>
												)
											)
									  )
									: data.map((categoryItem: ITableFormValue, categoryItemIndex: number) => (
											<Typography
												key={categoryItem.value}
												sx={{
													fontSize: '0.95rem',
													color: regularTextColor,
												}}
											>
												{`${categoryItemIndex + 1}. `}
												{renderTextWithDiff(
													oldTable[category][categoryItemIndex]?.value || '',
													newTable[category][categoryItemIndex]?.value || '',
													categoryItem.value || '',
													usedTable[category][categoryItemIndex]?.isDeleted || false,
													showStyles
												)}
											</Typography>
									  ))}
							</Box>
						</Box>
					)
				)}
			</Box>
		);
	};

	const getUseCasesBlock = ({
		newValue,
		oldValue,
		isNewBlock,
	}: {
		oldValue: Record<string, any>;
		newValue: Record<string, any>;
		isNewBlock: boolean;
	}) => {
		const usedValue = isNewBlock ? newValue : oldValue;

		return (
			<Box
				sx={{
					padding: '12px 16px',
					borderRadius: '8px',
					overflow: 'auto',
					backgroundColor: '#F3F4F580',
				}}
			>
				<Typography
					sx={{
						fontWeight: 700,
						marginBottom: { md: '8px', xl: '20px' },
					}}
				>
					{useCasesTranslate('generalText.useCase')} #1:{' '}
					{renderTextWithDiff(
						oldValue.title || '',
						newValue.title || '',
						usedValue.title || '',
						false,
						isNewBlock
					)}
				</Typography>

				{usedValue?.description ? (
					<Box sx={{ marginBottom: { md: '12px', xl: '20px' } }}>
						<Typography
							sx={{
								fontWeight: 700,
								margin: '0 10px 5px 0',
								color: titleTextColor,
							}}
						>
							{generalStepTranslate('commonText.description')}
						</Typography>
						<Typography
							sx={{
								wordWrap: 'break-word',
								whiteSpace: 'pre-line',
								paddingRight: '10px',
								color: regularTextColor,
							}}
						>
							{renderTextWithDiff(
								oldValue.description || '',
								newValue.description || '',
								usedValue.description || '',
								false,
								isNewBlock
							)}
						</Typography>
					</Box>
				) : null}

				<Box
					sx={{
						display: 'flex',
						width: '100%',
						justifyContent: 'flex-start',
						alignItems: 'center',
						position: 'relative',
						minHeight: '300px',
					}}
				>
					{getUseCaseTableBlock({
						oldTable: oldValue.table,
						newTable: newValue.table,
						showStyles: isNewBlock,
					})}
				</Box>

				{orderDiagrams(usedValue.diagrams)
					.filter((diagram) => diagram.pictureLink)
					.map((diagram, index) => (
						<Box key={index} data-testid={'diagram-block'}>
							<Typography
								sx={{
									fontWeight: 700,
									marginTop: { md: '8px', xl: '16px' },
									marginBottom: '4px',
									color: titleTextColor,
								}}
							>
								{useCasesTranslate(`diagrams.${diagram.title}`)}:{' '}
								{isDiagramUpdated({
									newDiagram: newValue.diagrams[index],
									oldDiagram: oldValue.diagrams[index],
									showStyles: isNewBlock,
								})
									? changesHistoryTranslate('text.updatedDiagram')
									: null}
							</Typography>
							<DiagramImage diagram={diagram} />
						</Box>
					))}
			</Box>
		);
	};

	return (
		<Box sx={{ display: 'flex', maxHeight: '90%', gap: '24px' }}>
			{getUseCasesBlock({ newValue, oldValue, isNewBlock: false })}
			{getUseCasesBlock({ newValue, oldValue, isNewBlock: true })}
		</Box>
	);
};
